import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { UsaStates } from 'usa-states';

import { withStyles } from '@material-ui/core/styles';
import withWidth from '@material-ui/core/withWidth';
import Grid from '@material-ui/core/Grid';
import Radio from '@material-ui/core/Radio';
import FormLabel from '@material-ui/core/FormLabel';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Typography from '@material-ui/core/Typography';

import { Field, FieldArray, formValueSelector } from 'redux-form';

import TextField from 'components/fields/TextField';
import AutosuggestField from 'components/fields/AutosuggestField';
import RadioGroupField from 'components/fields/RadioGroupField';
import DatepickerField from 'components/fields/DatepickerField';
import CheckboxField from 'components/fields/CheckboxField';
import FormError from 'components/common/FormError';

import PayerContactFieldArray from 'components/settings/PayerCreateEditDialog/PayerContactFieldArray';

import { requestSearchInsurances } from 'redux/modules/insurances';
import { accountLoginFail } from 'redux/modules/account';
import {
  requestFetchClaimFillingCodes,
  requestSearchClaimFillingCodes,
} from 'redux/modules/claimFillingCodes';

import * as validate from 'utils/validate';
import * as normalize from 'utils/normalize';

import dateFormat from 'config/dateFormat';
import transmissionMethods from 'config/transmissionMethods';
import insuranceRelationToInsured, { insuranceRelationToInsuredWorkersCompAuto } from 'config/insuranceRelationToInsured';
import insuranceOrderOfBenefits from 'config/insuranceOrderOfBenefits';

import InsuranceCreateEditFormSpecialDateFieldArray from './InsuranceCreateEditFormSpecialDateFieldArray';

const usaStates = new UsaStates();

const styles = () => ({
  grid: {
    marginTop: 10,
    '&:first-child': {
      marginTop: 0,
    },
  },
  copayAmount: {
    alignSelf: 'flex-end',
    marginBottom: 3,
  },
  autosuggestSuggestion: {
    maxWidth: 'none',
  },
});

const InsuranceCreateEditForm = ({
  handleSubmit,
  change,
  error,
  warning,
  className,
  claimFillingCodeID,
  copayAmountFixedDisabled,
  copayAmountPercentDisabled,
  relationToInsured,
  onAutosuggestFieldFocus,
  onAutosuggestFieldBlur,
  actions,
  classes,
  width,
  ...otherProps
}) => (
  <form
    noValidate
    autoComplete="off"
    onSubmit={handleSubmit}
    className={className}
  >
    {error &&
      <Grid
        className={classes.grid}
        container
        spacing={width === 'xs' || width === 'sm' ? 16 : 24}
      >
        <Grid
          item
          xs={12}
        >
          <FormError>
            {error}
          </FormError>
        </Grid>
      </Grid>
    }
    {warning &&
      <Grid
        className={classes.grid}
        container
        spacing={width === 'xs' || width === 'sm' ? 16 : 24}
      >
        <Grid
          item
          xs={12}
        >
          {warning.map(warningItem => (
            <FormError variant="warning">
              {warningItem}
            </FormError>
          ))} 
        </Grid>
      </Grid>
    }
    <Grid
      className={classes.grid}
      container
      justify="center"
      spacing={width === 'xs' || width === 'sm' ? 16 : 24}
    >
      <Grid
        item
        xs={12}
      >
        <Field
          id="Payer Name"
          name="Payer Name"
          label="Payer Name"
          component={AutosuggestField}
          validate={[validate.required]}
          errorPlacement="top"
          fullWidth
          renderSuggestionContent={suggestion => suggestion['Payer Name']}
          onSuggestionSelected={(suggestion) => {
            const phoneContacts = suggestion.Contacts
              ? suggestion.Contacts
              .filter(item => item.type === 'Phone')
              .map(item => ({
                Value: item.value,
                Description: item.description,
              })) : [];
            const faxContacts = suggestion.Contacts
              ? suggestion.Contacts
              .filter(item => item.type === 'Fax')
              .map(item => ({
                  Value: item.value,
                  Description: item.description,
                })) : [];
            const emailContacts = suggestion.Contacts
              ? suggestion.Contacts
              .filter(item => item.type === 'Email')
              .map(item => ({
                  Value: item.value,
                  Description: item.description,
                })) : [];
            change('Insurance Info ID', suggestion.ID);
            change('Insurance Info Payer Name', suggestion['Payer Name']);
            if (suggestion['Change Info']) {
              change('Is Available Eligibility', suggestion['Claim Md Payer'].Eligibility);
            }
            if (suggestion['Default Claim Filling Code'] && suggestion['Default Claim Filling Code'].ID) {
              change('Claim Filling Code Name', suggestion['Default Claim Filling Code']['Full Name']);
              change('Selected Claim Filling Code Name', suggestion['Default Claim Filling Code']['Full Name']);
              change('Claim Filling Code ID', suggestion['Default Claim Filling Code'].ID);
            }
            if (suggestion['Default Order Benefits']) {
              change('Order Benefits', suggestion['Default Order Benefits']);
            }
            if (suggestion['Default Transmission Method']) {
              change('Transmission Method', suggestion['Default Transmission Method']);
            }
            if (phoneContacts.length > 0) {
              change('Contacts Phone', phoneContacts);
            } else {
              change('Contacts Phone', [{
                Value: '',
                Description: '',
              }]);
            }
            if (faxContacts.length > 0) {
              change('Contacts Fax', faxContacts);
            } else {
              change('Contacts Fax', [{
                Value: '',
                Description: '',
              }]);
            }
            if (emailContacts.length > 0) {
              change('Contacts Email', emailContacts);
            } else {
              change('Contacts Email', [{
                Value: '',
                Description: '',
              }]);
            }
            return suggestion['Payer Name'];
          }}
          onFocus={() => {
            onAutosuggestFieldFocus();
          }}
          onBlur={() => {
            onAutosuggestFieldBlur();
          }}
          fetchSuggestions={({ value, limit, offset }) => (
            requestSearchInsurances({
              ...value.length > 0 ? { filters: [
                {
                  name: 'Payer Name',
                  operation: 'contains',
                  val: value,
                  case_sensitivity: false,
                },
                {
                  name: 'Is Active',
                  operation: 'eq',
                  val: true,
                },
              ] } : { filters: [
                {
                  name: 'Is Active',
                  operation: 'eq',
                  val: true,
                },
              ] },
              limit,
              offset,
            }).then(res => res.data,
              (err) => {
                if (err.data.error === 'Need authorisation') {
                  actions.accountLoginFail();
                }
                return err;
              })
          )}
          classes={{
            suggestion: classes.autosuggestSuggestion,
          }}
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      justify="center"
      spacing={width === 'xs' || width === 'sm' ? 16 : 24}
    >
      <Grid
        item
        xs={8}
      >
        <Field
          id="Claim Filling Code Name"
          name="Claim Filling Code Name"
          label="Payer Type"
          component={AutosuggestField}
          validate={[validate.required]}
          errorPlacement="top"
          fullWidth
          renderSuggestionContent={suggestion => suggestion['Full Name']}
          onSuggestionSelected={(suggestion) => {
            change('Claim Filling Code ID', suggestion.ID);
            change('Selected Claim Filling Code Name', suggestion['Full Name']);
            return suggestion['Full Name'];
          }}
          onFocus={() => {
            onAutosuggestFieldFocus();
          }}
          onBlur={() => {
            onAutosuggestFieldBlur();
          }}
          fetchSuggestions={({ value, limit, offset }) => (
            value.length > 0
              ? requestSearchClaimFillingCodes({
                filters: [
                  {
                    name: 'Full Name',
                    operation: 'startswith',
                    val: value,
                    case_sensitivity: false,
                  },
                ],
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
              : requestFetchClaimFillingCodes({
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
          )}
          classes={{
            suggestion: classes.autosuggestSuggestion,
          }}
        />
      </Grid>
      <Grid
        item
        xs={4}
      >
        <Field
          id="Order Benefits"
          name="Order Benefits"
          label="Order of Benefits"
          component={AutosuggestField}
          fullWidth
          errorPlacement="top"
          listWidth="auto-right"
          renderSuggestionContent={suggestion => suggestion}
          onSuggestionSelected={suggestion => suggestion}
          fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
            if (offset === 0) {
              if (value.length > 0) {
                resolve(insuranceOrderOfBenefits
                  .filter(item => item.toLowerCase().indexOf(value.toLowerCase()) !== -1));
              } else {
                resolve(insuranceOrderOfBenefits);
              }
            } else {
              resolve([]);
            }
          }))}
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      justify="center"
      spacing={width === 'xs' || width === 'sm' ? 16 : 24}
    >
      <Grid
        item
        xs={6}
      >
        <Field
          id="Insurance ID"
          name="Insurance ID"
          label="Insurance ID"
          component={TextField}
          validate={[validate.required]}
          maxLength="20"
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={6}
      >
        <Field
          id="Group ID"
          name="Group ID"
          label={(claimFillingCodeID && [2, 3].indexOf(claimFillingCodeID) !== -1) ? 'Claim Number' : 'Group ID'}
          component={TextField}
          maxLength="20"
          fullWidth
        />
      </Grid>
    </Grid>
    {(claimFillingCodeID && [2, 3].indexOf(claimFillingCodeID) !== -1) &&
      <FieldArray
        name="Insurance Special Date"
        component={InsuranceCreateEditFormSpecialDateFieldArray}
      />
    }
    <Grid
      className={classes.grid}
      container
      justify="flex-start"
      alignItems="flex-end"
      spacing={width === 'xs' || width === 'sm' ? 16 : 24}
    >
      <Grid
        item
        xs={6}
      >
        <Field
          id="Effective From"
          name="Effective From"
          label="Effective From"
          component={DatepickerField}
          normalize={normalize.date}
          fullWidth
          minDate="01/01/1900"
          maxDate="01/01/2100"
          dateFormat={dateFormat}
          placeholder="mm/dd/yyyy"
          errorPlacement="top"
          maxLength="10"
          endIcon="start"
        />
      </Grid>
      <Grid
        item
        xs={6}
      >
        <Field
          id="Effective To"
          name="Effective To"
          label="Effective To"
          component={DatepickerField}
          normalize={normalize.date}
          fullWidth
          minDate="01/01/1900"
          maxDate="01/01/2100"
          dateFormat={dateFormat}
          placeholder="mm/dd/yyyy"
          errorPlacement="top"
          maxLength="10"
          endIcon="end"
          horizontalPlacement="right"
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      justify="flex-start"
      alignItems="flex-end"
      spacing={width === 'xs' || width === 'sm' ? 16 : 24}
    >
      <Grid
        item
        xs={6}
      >
        <Field
          id="Transmission Method"
          name="Transmission Method"
          label="Transmission Method"
          component={AutosuggestField}
          validate={[validate.required]}
          placeholder="-Select Transmission Method-"
          errorPlacement="top"
          fullWidth
          renderSuggestionContent={suggestion => suggestion}
          onSuggestionSelected={suggestion => suggestion}
          fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
            if (offset === 0) {
              if (value.length > 0) {
                resolve(transmissionMethods
                  .filter(item => item.toLowerCase().indexOf(value.toLowerCase()) !== -1));
              } else {
                resolve(transmissionMethods);
              }
            } else {
              resolve([]);
            }
          }))}
        />
      </Grid>
      <Grid
        item
        xs={6}
      >
        <Field
          id="Signature On File"
          name="Signature On File"
          label="Signature On File"
          component={CheckboxField}
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      justify="flex-start"
      spacing={width === 'xs' || width === 'sm' ? 16 : 24}
    >
      <Grid
        item
        xs={6}
      >
        <Field
          id="Relation to Insured"
          name="Relation to Insured"
          label="Relation to Insured"
          component={AutosuggestField}
          fullWidth
          renderSuggestionContent={suggestion => suggestion}
          onSuggestionSelected={suggestion => suggestion}
          fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
            const suggestions = claimFillingCodeID && [2, 3].indexOf(claimFillingCodeID) !== -1
              ? insuranceRelationToInsuredWorkersCompAuto : insuranceRelationToInsured;
            if (offset === 0) {
              if (value.length > 0) {
                resolve(suggestions
                  .filter(item => item.toLowerCase().indexOf(value.toLowerCase()) !== -1));
              } else {
                resolve(suggestions);
              }
            } else {
              resolve([]);
            }
          }))}
        />
      </Grid>
      {(claimFillingCodeID && claimFillingCodeID === 3) &&
        <Grid
          item
          xs={4}
        >
          <Field
            id="Auto Accident State"
            name="Auto Accident State"
            label="Accident State"
            component={AutosuggestField}
            normalize={normalize.uppercase}
            fullWidth
            validate={[validate.required]}
            renderSuggestionContent={suggestion => suggestion.abbreviation}
            onSuggestionSelected={suggestion => suggestion.abbreviation}
            fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
              if (offset === 0) {
                if (value.length > 0) {
                  resolve(usaStates.states.filter(item =>
                    item.abbreviation.indexOf(value) === 0));
                } else {
                  resolve(usaStates.states);
                }
              } else {
                resolve([]);
              }
            }))}
          />
        </Grid>
      }
    </Grid> 
    {(relationToInsured && ['Self', 'Employee'].indexOf(relationToInsured) == -1) &&
      <Fragment>
        <Grid
          className={classes.grid}
          container
          justify="center"
          spacing={width === 'xs' || width === 'sm' ? 16 : 24}
        >
          <Grid
            item
            xs={6}
          >
            <Field
              id="Related First Name"
              name="Related First Name"
              label="Insured's First Name"
              component={TextField}
              validate={[validate.required]}
              fullWidth
            />
          </Grid>
          <Grid
            item
            xs={6}
          >
            <Field
              id="Related Last Name"
              name="Related Last Name"
              label="Insured's Last Name"
              component={TextField}
              validate={[validate.required]}
              fullWidth
            />
          </Grid>
        </Grid>
        <Grid
          className={classes.grid}
          container
          justify="flex-start"
          spacing={width === 'xs' || width === 'sm' ? 16 : 24}
        >
          <Grid
            item
            xs={6}
          >
            <Field
              id="Related Date of Birth"
              name="Related Date of Birth"
              label="Insured's Date of Birth"
              component={DatepickerField}
              validate={[validate.required, validate.date]}
              normalize={normalize.date}
              fullWidth
              minDate="01/01/1900"
              maxDate="01/01/2100"
              dateFormat={dateFormat}
              placeholder="mm/dd/yyyy"
              maxLength="10"
              endIcon="start"
            />
          </Grid>
          <Grid
            item
            xs={6}
          >
            <FormControl role="group">
              <FormLabel component="legend">
                Insured&apos;s Gender
              </FormLabel>
              <Field
                name="Related Gender"
                component={RadioGroupField}
              >
                <FormControlLabel
                  value="Male"
                  control={<Radio color="primary" />}
                  label="Male"
                />
                <FormControlLabel
                  value="Female"
                  control={<Radio color="primary" />}
                  label="Female"
                />
              </Field>
            </FormControl>
          </Grid>
        </Grid>
        <Grid
          className={classes.grid}
          container
          justify="flex-start"
          spacing={width === 'xs' || width === 'sm' ? 16 : 24}
        >
          <Grid
            item
            xs={6}
          >
            <Field
              id="Related Insurance ID"
              name="Related Insurance ID"
              label="Insured's Policy ID"
              component={TextField}
              validate={[validate.required]}
              fullWidth
            />
          </Grid>
        </Grid>
      </Fragment>
    }
    <Grid
      className={classes.grid}
      container
      justify="flex-start"
      spacing={width === 'xs' || width === 'sm' ? 16 : 24}
    >
      <Grid item>
        <FormControl role="group">
          <FormLabel component="legend">Copay Type</FormLabel>
          <Field
            name="Copay Type"
            component={RadioGroupField}
            direction="column"
          >
            <FormControlLabel
              value="None"
              control={<Radio color="primary" />}
              label="None"
            />
            <FormControlLabel
              value="Fixed"
              control={<Radio color="primary" />}
              label="Fixed"
            />
            <FormControlLabel
              value="Percent"
              control={<Radio color="primary" />}
              label="Percent"
            />
          </Field>
        </FormControl>
      </Grid>
      <Grid
        item
        xs={5}
        className={classes.copayAmount}
      >
        <Grid
          className={classes.grid}
          container
          justify="flex-end"
          direction="column"
          spacing={8}
        >
          <Grid
            item
          >
            <Field
              id="Copay Amount Fixed"
              name="Copay Amount Fixed"
              component={TextField}
              disabled={copayAmountFixedDisabled}
              placeholder="$0.00"
              fullWidth
              size="sm"
              errorPlacement="top"
              normalize={normalize.price}
            />
          </Grid>
          <Grid
            item
          >
            <Field
              id="Copay Amount Percent"
              name="Copay Amount Percent"
              component={TextField}
              disabled={copayAmountPercentDisabled}
              placeholder="0.00%"
              fullWidth
              size="sm"
              errorPlacement="top"
              normalize={normalize.percent}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      justify="flex-start"
      spacing={width === 'xs' || width === 'sm' ? 16 : 24}
    >
      <Grid
        item
        xs={12}
      >
        <Field
          id="Comments"
          name="Comments"
          label="Comments"
          component={TextField}
          multiline
          fullWidth
          rows={5}
        />
      </Grid>
    </Grid>
    <Grid
      container
      spacing={24}
    >
      <Grid
        item
        xs={12}
      >
        <FieldArray
          name="Contacts Phone"
          component={PayerContactFieldArray}
          variant="phone"
        />
      </Grid>
    </Grid>
    <Grid
      container
      spacing={24}
    >
      <Grid
        item
        xs={12}
      >
        <FieldArray
          name="Contacts Fax"
          component={PayerContactFieldArray}
          variant="fax"
        />
      </Grid>
    </Grid>
    <Grid
      container
      spacing={24}
    >
      <Grid
        item
        xs={12}
      >
        <FieldArray
          name="Contacts Email"
          component={PayerContactFieldArray}
          variant="email"
        />
      </Grid>
    </Grid>
    {otherProps['Is Available Eligibility'] &&
      <Grid
        className={classes.grid}
        container
        justify="center"
        spacing={width === 'xs' || width === 'sm' ? 16 : 24}
      >
        <Grid
          item
          xs={12}
        >
          <Typography variant="caption">Save insurance to make eligibility check</Typography>
        </Grid>
      </Grid>
    }
  </form>
);

InsuranceCreateEditForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  error: PropTypes.string,
  warning: PropTypes.string,
  className: PropTypes.string,
  claimFillingCodeID: PropTypes.number,
  copayAmountFixedDisabled: PropTypes.bool,
  copayAmountPercentDisabled: PropTypes.bool,
  relationToInsured: PropTypes.string.isRequired,
  onAutosuggestFieldFocus: PropTypes.func,
  onAutosuggestFieldBlur: PropTypes.func,
  classes: PropTypes.shape({
    grid: PropTypes.string.isRequired,
    copayAmount: PropTypes.string.isRequired,
    autosuggestSuggestion: PropTypes.string.isRequired,
  }).isRequired,
  actions: PropTypes.shape({
    accountLoginFail: PropTypes.func.isRequired,
  }).isRequired,
};

InsuranceCreateEditForm.defaultProps = {
  className: '',
  error: null,
  warning: null,
  claimFillingCodeID: 1,
  copayAmountFixedDisabled: false,
  copayAmountPercentDisabled: false,
  relationToInsured: 'Self',
  onAutosuggestFieldFocus: () => {},
  onAutosuggestFieldBlur: () => {},
};

const mapStateToProps = (store, ownProps) => {
  const selector = formValueSelector(ownProps.form);
  return {
    claimFillingCodeID: selector(store, 'Claim Filling Code ID'),
    copayAmountFixedDisabled: selector(store, 'Copay Type') !== 'Fixed',
    copayAmountPercentDisabled: selector(store, 'Copay Type') !== 'Percent',
    relationToInsured: selector(store, 'Relation to Insured'),
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ accountLoginFail }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withWidth()(withStyles(styles)(InsuranceCreateEditForm)));



// WEBPACK FOOTER //
// ./src/components/patient/InsuranceCreateEditDialog/InsuranceCreateEditForm.js